import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Check,
  ChevronDown,
  Copy,
  Link2,
  Loader2,
  Mail,
  Plus,
  Share2,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLE_LABEL = { member: "Member", admin: "Admin" };
const EXPIRY_OPTIONS = [
  { value: 1, label: "1 day" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
];
const MAX_USES_OPTIONS = [
  { value: 1, label: "1 use" },
  { value: 5, label: "5 uses" },
  { value: null, label: "Unlimited" },
];

function formatRelativeExpiry(iso) {
  if (!iso) return "—";
  try {
    const exp = new Date(iso);
    const now = new Date();
    const diff = (exp - now) / 1000;
    if (diff <= 0) return "expired";
    if (diff < 3600) return `expires in ${Math.max(1, Math.floor(diff / 60))}m`;
    if (diff < 86400)
      return `expires in ${Math.max(1, Math.floor(diff / 3600))}h`;
    return `expires in ${Math.max(1, Math.floor(diff / 86400))}d`;
  } catch {
    return "—";
  }
}

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to manual select */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export default function InvitesPanel({ workspace, currentUserRole }) {
  const isOwner = currentUserRole === "owner";
  const canInvite = isOwner || currentUserRole === "admin";

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Email-mode form
  const [emailValue, setEmailValue] = useState("");
  const [emailRole, setEmailRole] = useState("member");
  const [emailExpiry, setEmailExpiry] = useState(7);
  const [submittingEmail, setSubmittingEmail] = useState(false);

  // Link-mode form
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkRole, setLinkRole] = useState("member");
  const [linkExpiry, setLinkExpiry] = useState(7);
  const [linkMaxUses, setLinkMaxUses] = useState(5);
  const [submittingLink, setSubmittingLink] = useState(false);

  // Just-created invite to highlight + offer copy
  const [justCreated, setJustCreated] = useState(null);
  const [copiedTokens, setCopiedTokens] = useState({});

  // Revoke confirmation state
  const [revokeTarget, setRevokeTarget] = useState(null);

  const fetchInvites = useCallback(async () => {
    if (!workspace?.id || !canInvite) {
      setInvites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${workspace.id}/invites`);
      setInvites(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [workspace?.id, canInvite]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  if (!canInvite) return null;

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    const email = emailValue.trim().toLowerCase();
    if (!email || submittingEmail) return;
    setSubmittingEmail(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspace.id}/invites`,
        {
          mode: "email",
          email,
          role: emailRole,
          expires_in_days: emailExpiry,
        }
      );
      // Phase Polish — backend response shape is now uniform: always
      // `{invite: {...}}`. The branch that used to instant-add registered
      // users is gone (privacy: no email enumeration). Existing users
      // invited to a new workspace also click an email link to accept.
      if (data.invite) {
        toast.success(`Invite sent to ${email}`);
        setJustCreated(data.invite);
        await fetchInvites();
      }
      setEmailValue("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmittingEmail(false);
    }
  };

  const submitLink = async (e) => {
    e?.preventDefault?.();
    if (submittingLink) return;
    setSubmittingLink(true);
    try {
      const { data } = await api.post(
        `/workspaces/${workspace.id}/invites`,
        {
          mode: "link",
          role: linkRole,
          expires_in_days: linkExpiry,
          max_uses: linkMaxUses,
        }
      );
      if (data.invite) {
        setJustCreated(data.invite);
        toast.success("Shareable link ready — copy and send.");
        await fetchInvites();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmittingLink(false);
    }
  };

  const revoke = async (invite) => {
    try {
      await api.delete(
        `/workspaces/${workspace.id}/invites/${invite.id}`
      );
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      if (justCreated?.id === invite.id) setJustCreated(null);
      toast.success("Invite revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevokeTarget(null);
    }
  };

  const onCopy = async (invite) => {
    const ok = await copyToClipboard(invite.invite_url);
    if (ok) {
      setCopiedTokens((prev) => ({ ...prev, [invite.id]: true }));
      toast.success("Copied to clipboard");
      setTimeout(() => {
        setCopiedTokens((prev) => {
          const next = { ...prev };
          delete next[invite.id];
          return next;
        });
      }, 1800);
    } else {
      toast.error("Couldn't copy — long-press the URL to copy manually");
    }
  };

  return (
    <section
      className="space-y-3 mt-6"
      data-testid="settings-section-invites"
    >
      <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
        Invite people
      </h3>

      {/* Add by email */}
      <form
        onSubmit={submitEmail}
        className="space-y-2.5 p-4 rounded-xl border border-[#DCD7CE] bg-white"
        data-testid="invite-email-form"
      >
        <div className="flex items-center gap-2 mb-1">
          <Mail size={13} className="text-[#D96C4A]" strokeWidth={1.8} />
          <span className="font-sans text-xs uppercase tracking-[0.16em] text-[#2C2A29] font-semibold">
            Add by email
          </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            placeholder="teammate@somewhere.com"
            data-testid="invite-email-input"
            className="flex-1 px-3 py-2.5 rounded-lg bg-white border border-[#DCD7CE] font-sans text-sm text-[#2C2A29] placeholder:text-[#A5A29E] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
          />
          <SmallSelect
            value={emailRole}
            onChange={setEmailRole}
            options={[
              { value: "member", label: "Member" },
              ...(isOwner ? [{ value: "admin", label: "Admin" }] : []),
            ]}
            dataTestid="invite-email-role-select"
          />
          <SmallSelect
            value={emailExpiry}
            onChange={(v) => setEmailExpiry(Number(v))}
            options={EXPIRY_OPTIONS}
            dataTestid="invite-email-expiry-select"
          />
          <button
            type="submit"
            disabled={!emailValue.trim() || submittingEmail}
            data-testid="invite-email-submit"
            className="px-4 py-2.5 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium hover:bg-[#1A1918] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {submittingEmail ? "…" : "Add"}
          </button>
        </div>
        <p className="font-sans text-[11px] text-[#A5A29E] leading-relaxed">
          We&apos;ll email them an invite link. Once they accept, they&apos;ll
          appear in this workspace.
        </p>
      </form>

      {/* Or share a link */}
      <div
        className="rounded-xl border border-[#DCD7CE] bg-white overflow-hidden"
        data-testid="invite-link-card"
      >
        <button
          type="button"
          onClick={() => setLinkOpen((v) => !v)}
          data-testid="invite-link-toggle"
          aria-expanded={linkOpen}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#F8F6F1] transition-colors"
        >
          <span className="flex items-center gap-2">
            <Link2 size={13} className="text-[#D96C4A]" strokeWidth={1.8} />
            <span className="font-sans text-xs uppercase tracking-[0.16em] text-[#2C2A29] font-semibold">
              Or share an invite link
            </span>
          </span>
          <ChevronDown
            size={14}
            strokeWidth={1.8}
            className={`text-[#7A7571] transition-transform ${
              linkOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {linkOpen ? (
          <form
            onSubmit={submitLink}
            className="px-4 pb-4 pt-1 space-y-2"
            data-testid="invite-link-form"
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <SmallSelect
                value={linkRole}
                onChange={setLinkRole}
                options={[
                  { value: "member", label: "Member" },
                  ...(isOwner ? [{ value: "admin", label: "Admin" }] : []),
                ]}
                dataTestid="invite-link-role-select"
              />
              <SmallSelect
                value={linkExpiry}
                onChange={(v) => setLinkExpiry(Number(v))}
                options={EXPIRY_OPTIONS}
                dataTestid="invite-link-expiry-select"
              />
              <SmallSelect
                value={linkMaxUses === null ? "null" : linkMaxUses}
                onChange={(v) =>
                  setLinkMaxUses(v === "null" ? null : Number(v))
                }
                options={MAX_USES_OPTIONS.map((o) => ({
                  value: o.value === null ? "null" : o.value,
                  label: o.label,
                }))}
                dataTestid="invite-link-uses-select"
              />
              <button
                type="submit"
                disabled={submittingLink}
                data-testid="invite-link-submit"
                className="px-4 py-2.5 rounded-lg bg-[#2C2A29] text-white font-sans text-sm font-medium hover:bg-[#1A1918] disabled:opacity-50 transition flex items-center gap-1.5"
              >
                <Plus size={13} strokeWidth={2} />
                {submittingLink ? "…" : "Generate"}
              </button>
            </div>
            <p className="font-sans text-[11px] text-[#A5A29E] leading-relaxed">
              Anyone with the link can join until it's revoked, exhausted, or
              expires.
            </p>
          </form>
        ) : null}
      </div>

      {/* Just-created highlight */}
      {justCreated ? (
        <div
          className="rounded-xl border border-[#3A5A40]/30 bg-[#3A5A40]/8 px-4 py-3 space-y-2"
          data-testid="invite-just-created"
        >
          <p className="font-sans text-xs text-[#2C2A29]">
            <span className="font-semibold text-[#3A5A40]">Invite ready.</span>{" "}
            Send this URL to the person you want in {workspace.name}:
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={justCreated.invite_url || ""}
              onFocus={(e) => e.target.select()}
              data-testid="invite-just-created-url"
              className="flex-1 px-3 py-2 rounded-lg bg-white border border-[#DCD7CE] font-mono text-xs text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30"
            />
            <button
              type="button"
              onClick={() => onCopy(justCreated)}
              data-testid="invite-just-created-copy"
              className="px-3 py-2 rounded-lg bg-[#2C2A29] text-white font-sans text-xs font-medium hover:bg-[#1A1918] transition flex items-center gap-1.5"
            >
              {copiedTokens[justCreated.id] ? (
                <>
                  <Check size={12} strokeWidth={2} />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={12} strokeWidth={2} />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      ) : null}

      {/* Active invites list */}
      <div className="mt-2" data-testid="invites-list-wrap">
        <h4 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold mb-2">
          Active invites · {invites.length}
        </h4>
        {loading ? (
          <p className="font-sans text-sm text-[#7A7571] flex items-center gap-2">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </p>
        ) : invites.length === 0 ? (
          <p
            className="font-sans text-sm text-[#A5A29E] italic px-1"
            data-testid="invites-list-empty"
          >
            None pending. The active invites you create will appear here.
          </p>
        ) : (
          <ul
            className="divide-y divide-[#EFEAE0] border border-[#DCD7CE] rounded-xl overflow-hidden"
            data-testid="invites-list"
          >
            {invites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center gap-3 px-4 py-3 bg-white"
                data-testid={`invite-row-${inv.id}`}
              >
                <div className="shrink-0">
                  {inv.target_chat_id ? (
                    <Share2 size={14} className="text-[#1E7BFF]" strokeWidth={1.8} />
                  ) : inv.mode === "email" ? (
                    <Mail size={14} className="text-[#D96C4A]" strokeWidth={1.8} />
                  ) : (
                    <Link2 size={14} className="text-[#5B4C7A]" strokeWidth={1.8} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-sans text-sm font-medium text-[#2C2A29] truncate flex items-center gap-2">
                    {inv.mode === "email"
                      ? inv.invited_email
                      : inv.target_chat_id
                      ? "Chat share link"
                      : "Shareable link"}
                    {inv.target_chat_id ? (
                      <span
                        data-testid={`invite-row-chat-badge-${inv.id}`}
                        className="font-sans text-[9px] uppercase tracking-[0.15em] text-[#1E7BFF] bg-[#1E7BFF]/10 border border-[#1E7BFF]/25 rounded-full px-1.5 py-0.5"
                      >
                        chat
                      </span>
                    ) : null}
                  </div>
                  <div className="font-sans text-[11px] text-[#7A7571] truncate">
                    {ROLE_LABEL[inv.role] || inv.role} ·{" "}
                    {inv.mode === "link"
                      ? `${inv.uses_count} / ${inv.max_uses ?? "∞"} uses · `
                      : ""}
                    {formatRelativeExpiry(inv.expires_at)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(inv)}
                  data-testid={`invite-row-copy-${inv.id}`}
                  className="px-2.5 py-1.5 rounded-md border border-[#DCD7CE] text-[#2C2A29] hover:bg-[#F3F1EC] font-sans text-xs font-medium transition flex items-center gap-1.5"
                >
                  {copiedTokens[inv.id] ? (
                    <>
                      <Check size={12} strokeWidth={2} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={12} strokeWidth={2} />
                      Copy
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setRevokeTarget(inv)}
                  data-testid={`invite-row-revoke-${inv.id}`}
                  className="p-1.5 rounded-md text-[#A5A29E] hover:text-[#8C3A1F] hover:bg-[#FAF2F0] transition-colors"
                  title="Revoke this invite"
                  aria-label="Revoke this invite"
                >
                  <Trash2 size={14} strokeWidth={1.8} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent data-testid="invite-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Revoke this invite?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget?.mode === "email"
                ? `Anyone with this link can no longer join as ${revokeTarget?.invited_email}.`
                : `Anyone holding this link will get an "invite revoked" page when they try to use it.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-revoke-cancel">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeTarget && revoke(revokeTarget)}
              data-testid="invite-revoke-confirm"
              className="bg-[#8C3A1F] hover:bg-[#7A3019]"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function SmallSelect({ value, onChange, options, dataTestid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={dataTestid}
      className="px-3 py-2.5 rounded-lg bg-white border border-[#DCD7CE] font-sans text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 cursor-pointer"
    >
      {options.map((o) => (
        <option key={String(o.value)} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
